/**
 * Единый стор приложения: опубликованные данные, личный черновик, справочники.
 *
 * Поток данных: bootstrap → срез брендов → черновик. Любая правка уходит на сервер,
 * ответ (список затронутых правок) вливается в черновик, наложение пересобирается.
 * Раз в N секунд опрашиваем /api/revision: если на сервере появились новые пакеты,
 * показываем плашку «данные обновлены» — своё черновое при этом не теряется.
 */
import { create } from 'zustand'
import type {
  BootstrapResponse, Brand, BrandChannelPatch, Change, ChannelCode, CommentKind,
  Conflict, Draft, FieldDefinition, FieldScope, FieldType, MutationResponse,
} from '@shared/types.ts'
import { api, ApiRequestError } from '../lib/api.js'
import { queueEdit, type NewPendingEdit } from './retry-queue.js'
import { buildOverlay, emptyOverlay, type Overlay } from '../lib/draft-overlay.js'

export type Status = 'idle' | 'loading' | 'ready' | 'error'

/** Что нужно знать, чтобы завести колонку. Код собирается сам, из названия. */
export interface NewFieldInput {
  name: string
  type: FieldType
  scope: FieldScope
  /** Справочник значений — для списков с выбором. */
  enumSet?: string
  /** Код блока, в который встанет колонка. Пусто — блок «Поля». */
  group?: string
  /** Название блока, если он новый. */
  groupTitle?: string
}

export interface Toast {
  id: number
  kind: 'success' | 'error' | 'info'
  text: string
}

interface AppState {
  status: Status
  error: string | null

  bootstrap: BootstrapResponse | null
  brands: Brand[]
  brandsById: Map<number, Brand>
  draftChanges: Change[]
  draftNote: string
  conflicts: Conflict[]
  overlay: Overlay
  revision: number
  /** на сервере появились новые подтверждённые данные */
  staleRevision: number | null
  lastCommitAuthorId: number | null

  toasts: Toast[]
  committing: boolean

  load: () => Promise<void>
  reloadBrands: () => Promise<void>
  reloadDraft: () => Promise<void>
  startPolling: () => void
  stopPolling: () => void
  toast: (kind: Toast['kind'], text: string) => void
  dismissToast: (id: number) => void

  editBrand: (brandId: number, input: { name?: string; categoriesUnclear?: boolean; fields?: Record<string, unknown> }) => Promise<void>
  editChannel: (brandId: number, channel: ChannelCode, patch: BrandChannelPatch) => Promise<void>
  editCategoryChannel: (brandId: number, brandCategoryId: number, channel: ChannelCode, input: { isActive?: boolean; tier?: string | null }) => Promise<void>
  createBrand: (input: Parameters<typeof api.createBrand>[0]) => Promise<Brand | null>
  attachCategory: (brandId: number, categoryId: number) => Promise<void>
  /** Новый текстовый столбец из меню «Колонки»; возвращает код поля. */
  createField: (input: NewFieldInput) => Promise<string>
  /** Перечитать снимок настроек и каталога: после правки полей и заголовков. */
  reloadBootstrap: () => Promise<void>
  detachCategory: (brandId: number, brandCategoryId: number) => Promise<void>
  addComment: (
    brandId: number, body: string, channelCode?: ChannelCode | null,
    kind?: CommentKind, dueOn?: string | null, replyTo?: number | null,
  ) => Promise<void>
  /** Закрыть задачу: галочка плюс обязательный отчёт о выполнении. */
  completeTask: (commentId: number, report: string) => Promise<void>
  /** Перевести заметку в задачу и обратно. */
  setCommentKind: (commentId: number, kind: CommentKind, dueOn?: string | null) => Promise<void>
  archiveBrand: (brandId: number) => Promise<void>

  dropChange: (changeId: number) => Promise<void>
  /** Отменить последнюю правку черновика (Cmd+Z). Массовая правка снимается целиком. */
  undoLast: () => Promise<void>
  discardDraft: () => Promise<void>
  setDraftNote: (note: string) => void
  commit: (note?: string, resolutions?: { changeId: number; decision: 'mine' | 'theirs' | 'drop' }[]) => Promise<boolean>
}

let pollTimer: ReturnType<typeof setInterval> | null = null
let toastSeq = 1

/** Правки одной ячейки заменяют друг друга: сервер присылает актуальную версию. */
/** Кириллица → латиница для кода поля: «Срок поставки» → `srok_postavki`. */
const TRANSLIT: Record<string, string> = {
  а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'e', ж: 'zh', з: 'z', и: 'i',
  й: 'y', к: 'k', л: 'l', м: 'm', н: 'n', о: 'o', п: 'p', р: 'r', с: 's', т: 't',
  у: 'u', ф: 'f', х: 'h', ц: 'c', ч: 'ch', ш: 'sh', щ: 'sch', ъ: '', ы: 'y', ь: '',
  э: 'e', ю: 'yu', я: 'ya',
}

function translit(name: string): string {
  const code = [...name.trim().toLowerCase()]
    .map((char) => TRANSLIT[char] ?? char)
    .join('')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 36)
  /* Код обязан начинаться с латинской буквы: «2024» сервер не примет. */
  return /^[a-z]/.test(code) ? code : code === '' ? '' : `f_${code}`
}

function mergeChanges(existing: Change[], incoming: Change[]): Change[] {
  if (incoming.length === 0) return existing
  const byId = new Map(existing.map((c) => [c.id, c]))
  for (const change of incoming) byId.set(change.id, change)
  return [...byId.values()].sort((a, b) => a.seq - b.seq)
}

function errorText(err: unknown): string {
  if (err instanceof ApiRequestError) return err.message
  if (err instanceof Error) return err.message
  return 'Не удалось выполнить действие'
}

export const useApp = create<AppState>((set, get) => ({
  status: 'idle',
  error: null,
  bootstrap: null,
  brands: [],
  brandsById: new Map(),
  draftChanges: [],
  draftNote: '',
  conflicts: [],
  overlay: emptyOverlay(),
  revision: 0,
  staleRevision: null,
  lastCommitAuthorId: null,
  toasts: [],
  committing: false,

  async load() {
    set({ status: 'loading', error: null })
    try {
      const bootstrap = await api.bootstrap()
      const [brandsRes, draft] = await Promise.all([api.brands('active'), api.draft()])
      set({
        bootstrap,
        brands: brandsRes.brands,
        brandsById: new Map(brandsRes.brands.map((b) => [b.id, b])),
        draftChanges: draft.changes,
        draftNote: draft.note ?? '',
        conflicts: draft.conflicts,
        overlay: buildOverlay(draft.changes),
        revision: brandsRes.revision,
        staleRevision: null,
        status: 'ready',
      })
      get().startPolling()
    } catch (err) {
      set({ status: 'error', error: errorText(err) })
    }
  },

  async reloadBrands() {
    const res = await api.brands('active')
    set({
      brands: res.brands,
      brandsById: new Map(res.brands.map((b) => [b.id, b])),
      revision: res.revision,
      staleRevision: null,
    })
  },

  async reloadDraft() {
    const draft: Draft = await api.draft()
    set({
      draftChanges: draft.changes,
      draftNote: draft.note ?? '',
      conflicts: draft.conflicts,
      overlay: buildOverlay(draft.changes),
    })
  },

  startPolling() {
    const interval = (get().bootstrap?.settings.pollIntervalSec ?? 20) * 1000
    if (pollTimer) clearInterval(pollTimer)
    pollTimer = setInterval(() => {
      void api.revision()
        .then((res) => {
          if (res.revision > get().revision) {
            set({ staleRevision: res.revision, lastCommitAuthorId: res.lastCommit?.authorId ?? null })
          }
        })
        .catch(() => {/* сеть моргнула — просто ждём следующего опроса */})
    }, interval)
  },

  stopPolling() {
    if (pollTimer) clearInterval(pollTimer)
    pollTimer = null
  },

  toast(kind, text) {
    const toast: Toast = { id: toastSeq++, kind, text }
    set({ toasts: [...get().toasts, toast] })
    setTimeout(() => get().dismissToast(toast.id), kind === 'error' ? 8000 : 4000)
  },

  dismissToast(id) {
    set({ toasts: get().toasts.filter((t) => t.id !== id) })
  },

  // ── правки ────────────────────────────────────────────────────────────────
  async editBrand(brandId, input) {
    const label = get().brandsById.get(brandId)?.name ?? `Бренд #${brandId}`
    await applyMutation(set, get, () => api.updateBrand(brandId, input),
      { kind: 'brand', brandId, input: input as Record<string, unknown>, label })
  },

  async editChannel(brandId, channel, patch) {
    const label = get().brandsById.get(brandId)?.name ?? `Бренд #${brandId}`
    await applyMutation(set, get, () => api.updateChannel(brandId, channel, patch),
      { kind: 'channel', brandId, channel, patch, label })
  },

  async editCategoryChannel(brandId, brandCategoryId, channel, input) {
    const label = get().brandsById.get(brandId)?.name ?? `Бренд #${brandId}`
    await applyMutation(set, get, () => api.updateCategoryChannel(brandId, brandCategoryId, channel, input),
      { kind: 'categoryChannel', brandId, brandCategoryId, channel, input, label })
  },

  async createBrand(input) {
    const res = await applyMutation(set, get, () => api.createBrand(input))
    if (!res?.brand) return null
    const brand = res.brand
    set({
      brands: [...get().brands, brand].sort((a, b) => a.name.localeCompare(b.name, 'ru')),
      brandsById: new Map(get().brandsById).set(brand.id, brand),
    })
    return brand
  },

  async reloadBootstrap() {
    set({ bootstrap: await api.bootstrap() })
  },

  async createField(input) {
    /* Код поля человек не вводит: собираем из названия транслитом, а при
       совпадении добавляем номер — коды полей живут в API и в jsonb-значениях,
       менять их потом нельзя. */
    const existing = new Set((get().bootstrap?.fields ?? []).map((field) => field.code))
    const base = translit(input.name) || 'field'
    let code = base
    for (let n = 2; existing.has(code); n += 1) code = `${base}_${n}`

    const config: FieldDefinition['config'] = {}
    if (input.enumSet) config.enumSet = input.enumSet
    if (input.group && input.group !== 'fields') {
      config.group = input.group
      if (input.groupTitle) config.groupTitle = input.groupTitle
    }

    await api.createField({
      code,
      name: input.name.trim(),
      type: input.type,
      /* По умолчанию поле канала: у розницы и опта свои значения, как у всех
         прочих колонок таблицы. «Общее» значение — это выбор человека. */
      scope: input.scope,
      showByDefault: true,
      config,
    })
    /* Каталог колонок строится из bootstrap — перечитываем его целиком. */
    set({ bootstrap: await api.bootstrap() })
    return code
  },

  async attachCategory(brandId, categoryId) {
    const res = await applyMutation(set, get, () => api.attachCategory(brandId, categoryId))
    if (res?.brandCategory) {
      const brand = get().brandsById.get(brandId)
      if (brand) {
        const updated: Brand = {
          ...brand,
          categories: [...brand.categories, res.brandCategory].sort((a, b) => a.name.localeCompare(b.name, 'ru')),
        }
        replaceBrand(set, get, updated)
      }
    }
  },

  async detachCategory(brandId, brandCategoryId) {
    await applyMutation(set, get, () => api.detachCategory(brandId, brandCategoryId))
    // привязку из черновика сервер удаляет физически — убираем и у себя
    const brand = get().brandsById.get(brandId)
    if (brand && !get().overlay.detached.has(brandCategoryId)) {
      replaceBrand(set, get, { ...brand, categories: brand.categories.filter((c) => c.id !== brandCategoryId) })
    }
  },

  async completeTask(commentId, report) {
    const res = await applyMutation(set, get, () => api.completeTask(commentId, report))
    /* В таблице у бренда показан последний комментарий — после закрытия
       задачи это уже другой текст, и снимок надо перечитать. */
    if (res) await get().reloadBrands()
  },

  async setCommentKind(commentId, kind, dueOn = null) {
    const res = await applyMutation(set, get, () => api.setCommentKind(commentId, kind, dueOn))
    if (res) await get().reloadBrands()
  },

  async addComment(brandId, body, channelCode = null, kind = 'comment', dueOn = null, replyTo = null) {
    /* Ключ канала не передаём вовсе, если его нет: сервер принимает
       «retail»/«b2b» или отсутствие поля, а явный null считает ошибкой —
       комментарий ко всему бренду молча не проходил. */
    const label = get().brandsById.get(brandId)?.name ?? `Бренд #${brandId}`
    const res = await applyMutation(
      set, get,
      () => api.addComment(brandId, {
        body,
        ...(channelCode ? { channelCode } : null),
        ...(kind === 'task' ? { kind, ...(dueOn ? { dueOn } : null) } : null),
        ...(replyTo ? { replyTo } : null),
      }),
      { kind: 'comment', brandId, body, ...(channelCode ? { channel: channelCode } : null), label },
    )
    /* Колонка «Крайний комментарий» живёт в снимке брендов, а не в черновике:
       без перечитывания в ней остался бы прежний текст. */
    if (res) await get().reloadBrands()
  },

  async archiveBrand(brandId) {
    await applyMutation(set, get, () => api.archiveBrand(brandId))
  },

  // ── черновик ──────────────────────────────────────────────────────────────
  async dropChange(changeId) {
    try {
      await api.dropChange(changeId)
      await Promise.all([get().reloadDraft(), get().reloadBrands()])
    } catch (err) {
      get().toast('error', errorText(err))
    }
  },

  /**
   * Отмена последней правки. Массовая простановка записывается одной
   * операцией (`operationId`), и снимается она целиком: человек нажал один
   * раз — отменяет тоже один раз, а не двадцать.
   */
  async undoLast() {
    const changes = get().draftChanges
    const last = changes[changes.length - 1]
    if (!last) return
    const group = last.operationId
      ? changes.filter((change) => change.operationId === last.operationId)
      : [last]
    try {
      /* Последовательно, а не пачкой: порядок снятия важен для истории. */
      for (const change of group) await api.dropChange(change.id)
      await Promise.all([get().reloadDraft(), get().reloadBrands()])
      const what = last.fieldLabel ? `${last.entityLabel} · ${last.fieldLabel}` : last.entityLabel
      get().toast('info', group.length > 1
        ? `Отменено правок: ${group.length}`
        : `Отменено: ${what}`)
    } catch (err) {
      get().toast('error', errorText(err))
    }
  },

  async discardDraft() {
    try {
      await api.discardDraft()
      await Promise.all([get().reloadDraft(), get().reloadBrands()])
      get().toast('info', 'Черновик отменён')
    } catch (err) {
      get().toast('error', errorText(err))
    }
  },

  setDraftNote(note) {
    set({ draftNote: note })
    void api.setDraftNote(note).catch(() => {/* заметка не критична */})
  },

  async commit(note, resolutions) {
    set({ committing: true })
    try {
      const res = await api.commit({ note: note ?? get().draftNote, resolutions })
      await Promise.all([get().reloadBrands(), get().reloadDraft()])
      set({ committing: false, conflicts: [], draftNote: '' })
      get().toast(
        'success',
        res.telegram === 'queued' || res.telegram === 'sent'
          ? `Изменения опубликованы · пакет №${res.changeSetId} · уведомление отправлено`
          : `Изменения опубликованы · пакет №${res.changeSetId}`,
      )
      return true
    } catch (err) {
      set({ committing: false })
      if (err instanceof ApiRequestError && err.code === 'conflict') {
        set({ conflicts: err.conflicts })
        get().toast('error', err.message)
        return false
      }
      get().toast('error', errorText(err))
      return false
    }
  },
}))

type Setter = (partial: Partial<AppState>) => void
type Getter = () => AppState

/** Общая обёртка мутаций: влить правки в черновик, пересобрать наложение, показать ошибку. */
async function applyMutation(
  set: Setter, get: Getter, call: () => Promise<MutationResponse>,
  /* Чем повторить правку, если сеть моргнула. Без описания правка просто
     покажет ошибку — так ведут себя действия, которые нельзя повторять
     вслепую (подтверждение пакета, удаление). */
  retry?: NewPendingEdit,
): Promise<MutationResponse | null> {
  try {
    const res = await call()
    const merged = mergeChanges(get().draftChanges, res.changes)
    // правка могла исчезнуть (значение вернули к опубликованному) — сервер пришлёт draftCount
    const changes = res.draftCount === merged.length ? merged : await api.draft().then((d) => d.changes)
    set({ draftChanges: changes, overlay: buildOverlay(changes) })
    return res
  } catch (err) {
    /* Сервер ответил «так нельзя» — повторять бессмысленно, показываем причину.
       А вот сеть, таймаут или пятисотку повторяем: правка не должна пропасть
       из-за моргнувшего вайфая. */
    const fatal = err instanceof ApiRequestError
      && ['validation', 'forbidden', 'unauthorized', 'not_found', 'conflict'].includes(err.code)
    if (retry && !fatal) {
      queueEdit(retry)
      get().toast('error', `${errorText(err)} Правка сохранена и будет отправлена снова.`)
    } else {
      get().toast('error', errorText(err))
    }
    return null
  }
}

function replaceBrand(set: Setter, get: Getter, brand: Brand): void {
  set({
    brands: get().brands.map((b) => (b.id === brand.id ? brand : b)),
    brandsById: new Map(get().brandsById).set(brand.id, brand),
  })
}
